"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, } from "react";
import { cn } from "../utils/cn.js";
const TabsContext = createContext(null);
/**
 * Tabs — controlled or uncontrolled. Underline-only active marker per
 * brief §15 (no pill background). Arrow-Left / Arrow-Right cycle focus
 * and activation between tabs. Only the active tab is `tabindex=0`;
 * inactive tabs are `tabindex=-1` (WAI-ARIA tablist pattern).
 */
export function Tabs({ value, defaultValue, onValueChange, children, className, }) {
    const [internal, setInternal] = useState(value ?? defaultValue ?? "");
    const current = value ?? internal;
    const tabsRef = useRef([]);
    const idPrefix = useId();
    const idsRef = useRef(new Map());
    const setValue = useCallback((v) => {
        if (value === undefined)
            setInternal(v);
        onValueChange?.(v);
    }, [value, onValueChange]);
    /**
     * Tabs register themselves via callback ref. When `el` is null the tab
     * is unmounting — drop it so the keyboard-nav list stays current with
     * the rendered tablist.
     */
    const registerTab = useCallback((v, el) => {
        if (el === null) {
            tabsRef.current = tabsRef.current.filter((t) => t.value !== v);
            return;
        }
        const existing = tabsRef.current.find((t) => t.value === v);
        if (existing) {
            existing.el = el;
        }
        else {
            tabsRef.current.push({ value: v, el });
        }
    }, []);
    /**
     * Stable id allocator — both Tab and TabPanel derive the same
     * `(tabId, panelId)` pair from a value, so the bidirectional
     * aria-controls / aria-labelledby relationship is wired without
     * either half needing to know about the other.
     */
    const getTabIds = useCallback((v) => {
        let entry = idsRef.current.get(v);
        if (!entry) {
            entry = {
                tabId: `${idPrefix}-${v}-tab`,
                panelId: `${idPrefix}-${v}-panel`,
            };
            idsRef.current.set(v, entry);
        }
        return entry;
    }, [idPrefix]);
    return (_jsx(TabsContext.Provider, { value: { value: current, setValue, registerTab, tabsRef, getTabIds }, children: _jsx("div", { "data-slot": "tabs", className: className, children: children }) }));
}
export function TabsList({ children, className, ...rest }) {
    return (_jsx("div", { role: "tablist", "data-slot": "tablist", className: cn(
        // Scrollable on overflow so a wide tab set never blows out the page on
        // narrow viewports (hidden scrollbar — swipe/drag to reach more tabs).
        "relative flex gap-6 overflow-x-auto border-b border-ink-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className), ...rest, children: children }));
}
export function Tab({ value, children, className, disabled }) {
    const ctx = useContext(TabsContext);
    if (!ctx)
        throw new Error("Tab must be inside <Tabs>");
    const { tabId, panelId } = ctx.getTabIds(value);
    const active = ctx.value === value;
    // Unregister this tab on unmount so the arrow-key navigation list stays
    // accurate when conditional tabs come and go.
    useEffect(() => {
        return () => ctx.registerTab(value, null);
    }, [ctx, value]);
    const onKey = (e) => {
        const tabs = ctx.tabsRef.current;
        if (tabs.length === 0)
            return;
        const idx = tabs.findIndex((t) => t.value === ctx.value);
        if (idx < 0)
            return;
        if (e.key === "ArrowRight") {
            e.preventDefault();
            const next = tabs[(idx + 1) % tabs.length];
            ctx.setValue(next.value);
            next.el.focus();
        }
        else if (e.key === "ArrowLeft") {
            e.preventDefault();
            const prev = tabs[(idx - 1 + tabs.length) % tabs.length];
            ctx.setValue(prev.value);
            prev.el.focus();
        }
        else if (e.key === "Home") {
            e.preventDefault();
            const first = tabs[0];
            ctx.setValue(first.value);
            first.el.focus();
        }
        else if (e.key === "End") {
            e.preventDefault();
            const last = tabs[tabs.length - 1];
            ctx.setValue(last.value);
            last.el.focus();
        }
    };
    return (_jsxs("button", { role: "tab", id: tabId, "aria-selected": active, "aria-controls": panelId, tabIndex: active ? 0 : -1, type: "button", "data-slot": "tab", "data-active": active ? "true" : undefined, disabled: disabled, ref: (el) => ctx.registerTab(value, el), onClick: () => ctx.setValue(value), onKeyDown: onKey, className: cn(
        // shrink-0 + nowrap keeps each tab on one line so the list scrolls
        // horizontally instead of wrapping/squashing under the border.
        "relative -mb-px shrink-0 whitespace-nowrap py-3 text-body font-medium outline-none", "transition-colors duration-base ease-out", active ? "text-ink-900" : "text-ink-500 hover:text-ink-700", "focus-visible:shadow-focus rounded-sm", disabled && "opacity-50 cursor-not-allowed", className), children: [children, _jsx("span", { "aria-hidden": true, className: cn("absolute inset-x-0 -bottom-px h-0.5 bg-accent", "transition-opacity duration-base ease-out", active ? "opacity-100" : "opacity-0") })] }));
}
export function TabPanel({ value, children, className }) {
    const ctx = useContext(TabsContext);
    if (!ctx)
        throw new Error("TabPanel must be inside <Tabs>");
    if (ctx.value !== value)
        return null;
    const { tabId, panelId } = ctx.getTabIds(value);
    return (_jsx("div", { role: "tabpanel", id: panelId, "aria-labelledby": tabId, tabIndex: 0, "data-slot": "tabpanel", className: cn("py-4 outline-none", className), children: children }));
}
